import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OrganizationApi } from "~/functions/api/organization";
import type { Organization } from "~/interface/organization";
import { setOrganization } from "~/slices/auth";
import type { RootState } from "~/store";

// 組織データのキャッシュ（プロセス生存中）
const organizationCache = new Map<string, Organization>();

/** テスト間のキャッシュリークを防ぐためのクリア関数（テスト専用） */
export const __clearOrganizationCacheForTest = () => organizationCache.clear();

/**
 * 組織情報を取得し Redux に同期するフック。
 * - mount 時に auto-fetch（未ロード時のみ、キャッシュ優先）
 * - `refresh()` で強制再取得可能（生成失敗後のクレジット返還同期など）
 */
export const useOrganization = (organizationId: string | undefined) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const lastFetchedId = useRef<string | null>(null);
  const currentOrganization = useSelector((state: RootState) => state.auth.organization);

  /**
   * バックエンドから最新の組織情報を取得し Redux を更新する。
   * 戻り値で成功/失敗を返し、呼び出し側で UX 分岐可能。
   * 失敗時は `error` state にもメッセージを格納（初期ロード経路と挙動統一）。
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    if (!organizationId) return false;
    setIsLoading(true);
    setError(null);
    try {
      const response = await new OrganizationApi().get(organizationId);
      if (response !== null) {
        organizationCache.set(organizationId, response);
        dispatch(setOrganization(response)); // hasChanged 撤廃: 常時 dispatch
        lastFetchedId.current = organizationId;
        return true;
      }
      throw new Error("Failed to fetch organization data");
    } catch (err) {
      const message = err instanceof Error ? err.message : "組織データの取得に失敗しました";
      setError(message);
      console.error("useOrganization.refresh failed:", err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, dispatch]);

  // 初期ロード: Redux に未存在 & キャッシュになければ fetch
  useEffect(() => {
    if (!organizationId || lastFetchedId.current === organizationId) return;

    if (currentOrganization && currentOrganization.id === organizationId) {
      lastFetchedId.current = organizationId;
      return;
    }

    const cached = organizationCache.get(organizationId);
    if (cached && cached.id === organizationId) {
      dispatch(setOrganization(cached));
      lastFetchedId.current = organizationId;
      return;
    }

    refresh();
  }, [organizationId, currentOrganization, refresh, dispatch]);

  return { isLoading, error, refresh };
};
